<template>
  <div class="campaign-products-container">
    <!--
      ProductEditHost owns the product delete + stock dialog stack
      (ProductDeleteDialog, StockAvailabilityDialog) and all the state
      behind them. We just hand it `:campaign` + `:products` and wire
      ProductCard events to its exposed methods via $refs.editHost.

      `update:modal-popped` is inert since the 2026-09-01 sweep removed
      the legacy ProductCreationModal: the host now reports a constant
      false, so the "hide the rest of the tab" branch never fires. The
      event and its local mirror are kept so this surface's render path
      is untouched by that removal; retiring the chain is a follow-up.

      D2 (card https://trello.com/c/a7j1VV6D): the ProductCategoryManager
      combobox that used to sit above this host (authoring the flat
      campaign.product_categories list) is retired -- category NAMES are
      now authored exclusively on the Categories & Storefront page (the
      store_categories entity).
    -->
    <product-edit-host
      ref="editHost"
      :campaign="campaign"
      :products="campaignProducts"
      @campaign-changed="onCampaignChanged"
      @product-upload="onProductUpload"
      @update:modal-popped="onModalPoppedChanged"
    />
    <div
      v-if="!productEditModalPopped && !productsPending"
      class="shopper-store-margin"
    >
      <div class="products-toolbar">
        <v-btn
          @click="addProduct"
          :disabled="!!campaign && !!campaign.complete || creatingProduct"
          rounded="pill"
          size="large"
          class="create-product-btn extended-fab elevation-3"
          color="primary"
          data-testid="ct-products-create-add"
        >
          <v-icon color="white" class="mr-2 extended-fab-icon" icon="add" />
          <v-spacer />
          <div style="color: white">Create Product</div>
        </v-btn>
        <!--
          Boulders: Products page: tabular view, https://trello.com/c/R0K6f6oC.
          Card/table toggle -- additive beside the existing card grid, which
          stays the default and is unaffected when the toggle is untouched.
          Mirrors the List/Grid toggle pattern in DecorationLibraryList.vue
          (dl-mode-toggle) rather than inventing a new toggle affordance.
        -->
        <div
          class="products-view-toggle"
          role="group"
          aria-label="Product view"
          data-testid="products-view-toggle"
        >
          <button
            type="button"
            class="products-view-toggle__btn"
            :class="{ 'products-view-toggle__btn--active': viewMode === 'cards' }"
            data-testid="products-view-toggle-cards"
            aria-label="Card view"
            @click="setViewMode('cards')"
          >
            <v-icon size="18" icon="grid_view" />
            Cards
          </button>
          <button
            type="button"
            class="products-view-toggle__btn"
            :class="{ 'products-view-toggle__btn--active': viewMode === 'table' }"
            data-testid="products-view-toggle-table"
            aria-label="Table view"
            @click="setViewMode('table')"
          >
            <v-icon size="18" icon="view_list" />
            Table
          </button>
        </div>
      </div>
      <products-table-view
        v-if="viewMode === 'table'"
        :campaign="campaign"
        :campaign-products="campaignProducts"
        :readiness-by-campaign-product-id="readinessByCampaignProductId"
        :incomplete-product-list="incompleteProductList"
        @readiness-click="onCardReadinessClick"
      />
      <v-form v-else ref="form" v-model="productsValid">
        <v-row align-content="space-around">
          <v-col
            v-for="(campaignProduct, index) in campaignProducts"
            :key="index"
            class="d-flex flex-column align-stretch"
            cols="12"
            sm="6"
            md="4"
          >
            <v-text-field :rules="campaignProductRules(index)" v-show="false" />
            <!--
              Phase 4.4 -- per-card selection checkbox. Positioned as
              an overlay at the top-left of each ProductCard via a
              wrapping flex container; the box is opt-in (clicking the
              card itself goes to the editor as before). When a card
              is in the active selection it gets a subtle ring.
            -->
            <div
              class="bulk-select-card-wrap"
              :class="{
                'bulk-select-card-wrap--selected':
                  productsListSelection.isSelected(_cpId(campaignProduct)),
              }"
            >
              <label
                class="bulk-select-card-checkbox"
                :data-testid="`products-multi-select-row-${_cpId(campaignProduct)}`"
                @click.stop="onCardSelectionToggle(_cpId(campaignProduct))"
              >
                <input
                  type="checkbox"
                  :checked="productsListSelection.isSelected(_cpId(campaignProduct))"
                  @click.stop="onCardSelectionToggle(_cpId(campaignProduct))"
                  :aria-label="`Select ${campaignProduct.product_name || 'product'}`"
                />
              </label>
              <product-card
                class="flex-grow-1 d-flex flex-column"
                style="flex-direction: column"
                :key="`product-card-${campaignProduct.product.id}`"
                :ref="`product-card-${index}`"
                :campaign="campaign"
                :campaignProduct="campaignProduct"
                :index="index"
                :productIncomplete="incompleteProductList[index]"
                :readiness="readinessByCampaignProductId[campaignProduct.id] || null"
                @delete-product="onCardDeleteProduct"
                @stockLoaded="onCardStockLoaded"
                @hide-from-storefront-changed="onCardHideFromStorefrontChanged"
                @readiness-click="onCardReadinessClick"
              />
            </div>
          </v-col>
          <v-card
            height="200"
            width="100%"
            elevation="0"
            v-if="fetchingProducts"
          >
            <div class="d-flex justify-center align-center ma-5">
              <v-progress-circular :active="true" :indeterminate="true" />
            </div>
          </v-card>
        </v-row>
      </v-form>
    </div>
    <!--
      Phase 4.4 -- bulk action bar + modal stack. The bar self-hides
      when no products are selected (see BulkActionBar.vue v-if).
      Modals are mounted unconditionally; their internal v-if guards
      keep the DOM clean when closed.
    -->
    <BulkActionBar
      :selectedCount="bulkSelectedCount"
      @apply-set="onBulkApplySet"
      @duplicate="onBulkDuplicateClick"
      @set-visibility="onBulkVisibilityClick"
    />
    <BulkVisibilityModal
      v-model="bulkVisibilityModalOpen"
      :selectedCount="bulkSelectedCount"
      @submit="onBulkVisibilitySubmit"
    />
    <BulkDuplicateModal
      v-model="bulkDuplicateModalOpen"
      :sourceProducts="bulkSelectedSourceProducts"
      :existingNames="bulkExistingProductNames"
      @submit="onBulkDuplicateSubmit"
    />
    <!--
      Phase 4.6 -- bulk-apply-set modal. selectedSetId is local state
      seeded by the DecorationSetPicker (mounted inside the modal). The
      modal fetches preview_apply via its own Pinia store and calls the
      apply endpoint on confirm; this parent only owns open/close +
      forwards the applied event for a list refresh.
    -->
    <BulkApplySetModal
      v-model="bulkApplyModalOpen"
      v-model:selectedSetId="bulkApplySetId"
      :selectedProductIds="bulkSelectedIds"
      @applied="onBulkApplyApplied"
    />
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import rules from "@/mixins/rules";
import ProductCard from "./ProductCard";
import TabError from "../TabError.vue";
import helpers from "@/mixins/helpers";
import LinkBubble from "@/components/LinkBubble.vue";
import ProductEditHost from "../ProductEditHost.vue";
import ProductsTableView from "./ProductsTableView.vue";
import { FIRST_TAB_SLUG } from "./Editor/productEditorTabRoutes";
import { useStoreReadinessStore } from "@/stores/storeReadiness";
import {
  persistViewModePreference,
  readViewModePreference,
} from "./viewModePreference";
// Phase 4.4 -- bulk action bar wiring. The multi-select state lives in
// the productsListSelection Pinia store; this page is the only mounted
// consumer of the bar (per the implementation plan section 4.1).
import { useProductsListSelection } from "@/stores/productsListSelection";
import BulkActionBar from "@/components/distributor/storeProducts/BulkActionBar.vue";
import BulkVisibilityModal from "@/components/distributor/storeProducts/BulkVisibilityModal.vue";
import BulkDuplicateModal from "@/components/distributor/storeProducts/BulkDuplicateModal.vue";
import BulkApplySetModal from "@/components/distributor/storeProducts/BulkApplySetModal.vue";

// Boulders: Products page: tabular view, https://trello.com/c/R0K6f6oC.
// Guarded read of the per-user id localStorage key this file already uses
// elsewhere (loadCampaignProductsLazy). Unlike that call site (reached only
// after mount, inside an async loop many tests never trigger), this one
// runs synchronously in data() on EVERY mount, so an unguarded
// localStorage.getItem would crash any test/environment where
// window.localStorage is absent (jsdom's default -- no --localstorage-file)
// -- readViewModePreference() already tolerates a missing/corrupt VALUE,
// but can't help if the ARGUMENT expression itself throws first.
function currentUserIdForViewMode() {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem("userId") : null;
  } catch {
    return null;
  }
}

// Issue #3: the "Create Product" CTA now creates a CampaignProduct row
// with minimal defaults (name "New product", no SKU, no supplier) and
// routes straight to the canonical product editor at
// /distributor/product-editor/:campaignProductId. Supplier + SKU are
// picked inside the editor on the Supplier & SKU view. The legacy
// ProductCreationCard (which asked for name + supplier + SKU up front
// and chained into the legacy ProductCreationModal) was deleted in the
// 2026-09-01 dead-code sweep, along with the modal itself -- the
// edit-existing path routes to the canonical editor too.

export default {
  props: {
    campaign: Object,
    editMode: Boolean,
  },

  mixins: [rules, helpers],

  components: {
    "product-card": ProductCard,
    TabError,
    LinkBubble,
    "product-edit-host": ProductEditHost,
    "products-table-view": ProductsTableView,
    BulkActionBar,
    BulkVisibilityModal,
    BulkDuplicateModal,
    BulkApplySetModal,
  },

  setup() {
    // Phase 4.4 -- selection state. Exposing the store from setup()
    // keeps the rest of the Options API component idiomatic.
    const productsListSelection = useProductsListSelection();
    return { productsListSelection };
  },

  watch: {
    productEditModalPopped: {
      async handler(newVal, oldVal) {
        this.$store.commit("setProductEditModalPopped", newVal);
        // dialog closed
        if (!this.productEditModalPopped) {
          this.loadCampaignProducts();
        } else {
        }
      },
    },
    // Phase 4.4 -- keep the selection store's allIds list in sync
    // with the visible products. When the list reloads (e.g. after a
    // bulk action) any selected ids no longer present are pruned by
    // ``setAllIds``. Watcher is deep+immediate so we catch the lazy-
    // loaded id-stream as it grows.
    campaignProducts: {
      handler() {
        const ids = (this.campaignProducts || [])
          .map((cp) => this._cpId(cp))
          .filter((x) => x !== null && x !== undefined);
        this.productsListSelection.setAllIds(ids);
      },
      deep: false,
    },
  },

  data() {
    return {
      valid: true,
      productsValid: true,

      // Issue #3: in-flight guard for the new stub-to-editor CTA.
      // Disables the button while the POST is outstanding so a
      // double-click cannot create two orphan CampaignProduct rows.
      creatingProduct: false,
      // Mirror of ProductEditHost's modalPopped state, which is now a
      // constant false (the legacy modal is gone). Kept so the watcher
      // above and this surface's render path are unchanged.
      productEditModalPopped: false,

      campaignProducts: [],
      // Boulders: Products page: tabular view, https://trello.com/c/R0K6f6oC.
      // "cards" is the default and preserves today's behavior exactly when
      // the toggle is untouched. Persisted per-user (ALL stores) via
      // localStorage -- restored synchronously here (before first render,
      // avoiding a cards->table flash) from the SAME per-user "userId" key
      // this file already reads elsewhere (loadCampaignProductsLazy). See
      // viewModePreference.js for the storage semantics + setViewMode()
      // below, which persists on every toggle.
      viewMode: readViewModePreference(currentUserIdForViewMode()),
      // Phase 2.2: per-campaign-product readiness rollup (keyed by
      // campaign_product_id). Populated by loadProductsReadiness();
      // empty object means "not yet fetched" so ProductCard renders
      // without the flag (legacy-safe default).
      readinessByCampaignProductId: {},
      products: {},
      productTypes: [],

      productsPending: false,
      productsLoaded: false,

      offset: 0,
      fetchingProducts: false,
      productCount: 0,

      productSKUs: [],
      cancelingEdit: false,
      canLoadProducts: true,

      // Phase 4.4 -- modal-open state for the bulk-action bar.
      // Selection itself lives in the productsListSelection Pinia
      // store; these flags are local UI only.
      bulkVisibilityModalOpen: false,
      bulkDuplicateModalOpen: false,
      bulkSubmitting: false,
      // Phase 4.6 -- bulk-apply-set modal state. selectedSetId is
      // local because the picker is mounted inside the modal; we
      // pass it down with v-model:selectedSetId so the modal can
      // forward the user's pick.
      bulkApplyModalOpen: false,
      bulkApplySetId: null,
    };
  },

  async mounted() {
    await this.loadProductTypes();
    await this.getProductCount();
  },

  computed: {
    // Phase 4.4 -- expose selection state to the template.
    bulkSelectedCount() {
      return this.productsListSelection.selectedCount;
    },
    bulkSelectedSourceProducts() {
      // Source list for the duplicate modal preview. Map selected ids
      // back to {id, product_name} entries from the visible list.
      const selected = new Set(
        this.productsListSelection.selectedIdArray,
      );
      return (this.campaignProducts || [])
        .filter((cp) => selected.has(this._cpId(cp)))
        .map((cp) => ({
          id: this._cpId(cp),
          product_name: cp.product_name || '',
        }));
    },
    // Phase 4.6 -- the list of selected CP ids the apply modal needs.
    // BulkApplySetModal expects an array of numbers; the selection
    // store gives us that shape via selectedIdArray.
    bulkSelectedIds() {
      return this.productsListSelection.selectedIdArray.slice();
    },
    bulkExistingProductNames() {
      // Pass the full visible-list of names to the duplicate modal so
      // its preview can match the server's macOS-increment behavior.
      return (this.campaignProducts || [])
        .map((cp) => cp.product_name)
        .filter((n) => typeof n === 'string' && n.length > 0);
    },
    incompleteProductList() {
      const list = [];
      if (this.campaignProducts.length > 0) {
        this.campaignProducts.forEach((product, index) => {
          let incomplete = false;
          if (
            this.$store.getters.getSmartShippingEnabled &&
            (!product.product_weight || product.product_weight == 0)
          ) {
            incomplete = true;
          }
          list[index] = incomplete;
        });
      } else {
        for (let index = 0; index < this.productCount; index++) {
          list[index] = false;
        }
      }

      return list;
    },
  },

  methods: {
    // Boulders: Products page: tabular view, https://trello.com/c/R0K6f6oC.
    // Local UI state, persisted per-user (ALL stores) via localStorage --
    // see viewModePreference.js. No server round trip.
    setViewMode(mode) {
      if (mode !== "cards" && mode !== "table") return;
      this.viewMode = mode;
      persistViewModePreference(currentUserIdForViewMode(), mode);
    },

    async validateTab() {
      await this.getProductCount();
      let invalid;
      if (this.productCount == 0) {
        invalid = true;
      } else {
        invalid = await this.getIsProductsIncomplete();
      }
      return !invalid;
    },

    async getIsProductsIncomplete() {
      let result;
      result = await this.$axios.get(
        `/campaign/getIsProductsIncomplete/${this.campaign._jv.id}`
      );
      const incomplete = result && result.data;
      this.$store.commit("setProductsIncomplete", incomplete);
      return incomplete;
    },

    campaignProductRules(index) {
      const rules = [];
      rules.push(!this.incompleteProductList[index] || "");

      return rules;
    },

    async getProductCount() {
      let count_result;
      if (this.$props.campaign.id) {
        try {
          count_result = await this.$axios.get(
            `/product/getCampaignProductCount/${
              this.$props.campaign._jv.id
            }/${true}`
          );
        } catch (err) {
          console.log(describeError(err));
          this.$store.dispatch("raiseError", err);
          throw err;
        }
        this.productCount = count_result.data.count;
      }
    },

    clearProducts() {
      this.valid = this.productsValid;
      this.campaignProducts = [];
      this.offset = 0;
      this.fetchingProducts = false;
      this.productsLoaded = false;
    },

    // Issue #3: stub create-then-edit. POST a minimal CampaignProduct
    // row (name "New product", no SKU, no supplier) against the SAFRS
    // endpoint, then push directly to the canonical product editor.
    // Errors flow through the existing raiseError snackbar pipeline so
    // the user gets feedback if the create fails. The legacy
    // ProductCreationCard + ProductCreationModal chain this replaced was
    // deleted in the 2026-09-01 dead-code sweep.
    async addProduct() {
      if (this.creatingProduct) return;
      if (!this.campaign || !this.campaign._jv || !this.campaign._jv.id) {
        // Defensive: the CTA's :disabled guard normally catches this,
        // but if the store row hasn't hydrated for any reason we don't
        // want to POST a CampaignProduct with no parent.
        this.$store.dispatch(
          "raiseError",
          new Error("Cannot create product: store is not loaded"),
        );
        return;
      }
      this.creatingProduct = true;
      this.$store.commit("setProductSaving", false);
      try {
        // Use the custom /campaign_product/create_stub endpoint
        // instead of the SAFRS POST. The SAFRS auto-POST handler's
        // ``_add_rels`` helper treats relationship payloads as
        // create-or-update via ``_s_post``, which (with
        // ``allow_client_generated_ids=False``) silently creates a
        // new Store row instead of linking to the provided id —
        // attaching the new CampaignProduct to that phantom store.
        // The custom endpoint takes a plain campaign_id and tenant-
        // gates the parent store explicitly. See the endpoint
        // docstring in api/src/endpoints/campaign_product.py.
        const response = await this.$axios.post(
          "/campaign_product/create_stub",
          {
            campaign_id: this.campaign._jv.id,
            name: "New product",
          },
        );
        const newId = response && response.data && response.data.id;
        if (newId == null) {
          throw new Error(
            "Create product succeeded but server returned no id",
          );
        }
        // Force the first tab (Supplier & SKU) on CREATE. Without an
        // explicit :tab the editor's route-sync falls back to the
        // localStorage last-viewed tab, so a brand-new product would open
        // on whatever tab was last visited. Editing an existing product
        // (ProductCard pencil) intentionally omits :tab to keep that
        // last-viewed behavior.
        this.$router.push({
          name: "product_editor",
          params: {
            campaignProductId: String(newId),
            tab: FIRST_TAB_SLUG,
          },
        });
      } catch (err) {
        // Keep the surface mounted so the user can retry; raiseError
        // surfaces the snackbar through the existing error pipeline.
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      } finally {
        this.creatingProduct = false;
      }
    },

    async loadProductTypes() {
      this.productsPending = true;

      try {
        const productTypes = await this.$store.dispatch("jv/get", [
          "product_types",
          { params: { include: "primary_attribute,secondary_attribute" } },
        ]);
        this.productTypes = Object.values(productTypes);
      } catch (err) {
        throw err;
      } finally {
        this.productsPending = false;
      }
    },

    loadCampaignProducts() {
      this.canLoadProducts = true;
      this.productSKUs = [];
      this.clearProducts();
      this.fetchingProducts = true;
      this.loadCampaignProductsLazy();
      // Phase 2.2: kick the per-product readiness rollup. Independent
      // from product loading so an error there doesn't blank the
      // page; the badge is additive, not gating.
      this.loadProductsReadiness();
    },

    async loadProductsReadiness() {
      const storeId = this.$props.campaign?._jv?.id;
      if (!storeId) return;
      try {
        const readinessStore = useStoreReadinessStore();
        const rows = await readinessStore.fetchProductsReadiness(storeId);
        const map = {};
        (rows || []).forEach((r) => {
          map[r.campaign_product_id] = r;
        });
        this.readinessByCampaignProductId = map;
      } catch (err) {
        // Non-fatal: log + leave the map empty so cards render
        // without the badge. We do NOT raise into the global error
        // banner -- a readiness-fetch failure shouldn't block the
        // products list from rendering.
        // eslint-disable-next-line no-console
        console.warn("loadProductsReadiness failed", describeError(err));
      }
    },

    onCardReadinessClick(payload) {
      // Navigate to the Approval Workspace filtered to this product.
      // The workspace consumes filter params per ADR 0039 / P2-Q3.
      const cpid = payload?.campaignProductId;
      if (!cpid) return;
      this.$router.push({
        path: "/distributor/decorations/approvals",
        query: { filter: `product:${cpid}` },
      });
    },

    async loadAllProducts() {
      this.fetchingProducts = true;
      let result;
      try {
        if (this.$props.campaign._jv.id) {
          result = await this.$axios.get(
            `/product/getCampaignProductList/${this.$props.campaign._jv.id}`
          );
        }
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
        throw err;
      } finally {
        this.productsLoaded = true;
        this.campaignProducts = result ? result.data : [];
        this.campaign.campaign_products = this.campaignProducts;
        this.fetchingProducts = false;
      }
    },

    stopLoadingProducts() {
      this.canLoadProducts = false;
    },

    async loadCampaignProductsLazy() {
      // keep retreiving orders until the returned list is empty
      const retrievalCount = 5;
      let result;
      this.productsLoaded = false;

      while (
        !this.productsLoaded &&
        this.canLoadProducts &&
        this.$props.campaign._jv.id &&
        localStorage.getItem("userId")
      ) {
        this.valid = this.productsValid;
        try {
          if (this.$props.campaign._jv.id) {
            result = await this.$axios.get(
              `/product/getCampaignProductsLazy/${
                this.$props.campaign._jv.id
              }/${retrievalCount}/${this.offset}/${true}`
            );
            if (result.data.length > 0) {
              for (const product of result.data) {
                // The lazy endpoint returns flat dicts without JSON:API _jv
                // wrapper. Add it so editProduct and save paths can use _jv.id.
                if (!product._jv && product.id) {
                  product._jv = { id: product.id, type: "CampaignProduct" };
                }
                this.productSKUs.push(product.product_item_number);
                this.campaignProducts.push(product);
              }
            }
          }
        } catch (err) {
          console.log(describeError(err));
          this.$store.dispatch("raiseError", err);
          throw err;
        } finally {
          this.offset += retrievalCount;
          // when all products in campaign have been loaded
          if (result.data.length < retrievalCount) {
            this.productsLoaded = true;
            this.$store.commit("setProductsLoaded", true);
            this.fetchingProducts = false;
          }
        }
      }
      this.fetchingProducts = false;
    },

    async loadCampaignProducts_OLD() {
      if (!this.productsLoaded) {
        this.productsPending = true;
        try {
          if (this.$props.campaign._jv.id) {
            const result = await this.$axios.get(
              `/product/getCampaignProductList/${this.$props.campaign._jv.id}`
            );
            this.campaignProducts = result.data;
          }
        } catch (err) {
          console.log(describeError(err));
          this.$store.dispatch("raiseError", err);
          throw err;
        } finally {
          this.productsPending = false;
          this.productsLoaded = true;
          this.campaign.campaign_products = this.campaignProducts;
        }
      }
    },

    updateCampaign(campaign) {
      campaign.error = null;
      // if leaving current tab, or saving from current tab
      if (!(this.productCount > 0 && this.campaignProducts.length == 0)) {
        this.valid = this.productsValid;
      } else {
        this.valid = !this.incompleteProductList.includes(true);
      }

      if (!this.valid) {
        campaign.error = "Products contain incomplete information";
      }

      campaign.campaign_products = this.campaignProducts;
      // D2 (card https://trello.com/c/a7j1VV6D): this used to overwrite
      // campaign.product_categories with the (now-removed) local
      // productCategories authoring state. Leaving the field untouched
      // here so this tab no longer clobbers it -- category NAMES are
      // authored on the Categories & Storefront page (the
      // store_categories entity), not here.
      return campaign;
    },

    // ---------- ProductEditHost integration ----------

    // The host emits this when its modalPopped changes. We mirror it
    // onto our data field so the existing productEditModalPopped watcher
    // (above) keeps working: hide the grid while popped, reload products
    // when the modal closes.
    onModalPoppedChanged(val) {
      this.productEditModalPopped = !!val;
    },

    onCampaignChanged(productList) {
      // Re-emit upward for any ancestor listeners that mirror the campaign
      // (store-edit shell, autosave, etc.).
      this.$emit("campaign-changed", productList);
      // Issue #2218: the delete composable splices ProductEditHost's private
      // ref(props.products) copy, NOT this component's own reactive
      // campaignProducts (the v-for source). Re-emitting alone left the
      // deleted card on screen until a manual refresh. Reload the
      // authoritative list from the server so the rendered grid reflects the
      // real campaign-product set (single source of truth). loadCampaignProducts
      // hydrates via $axios.get and does NOT emit campaign-changed, so there
      // is no re-entrant loop.
      this.loadCampaignProducts();
    },

    onProductUpload(val) {
      this.$emit("product-upload", val);
    },

    // ---------- ProductCard event forwarders ----------
    //
    // Each of these forwards a single ProductCard event into the host's
    // exposed API. We use named handlers (not inline `$refs.editHost.x`)
    // so that:
    //   1. If editHost isn't mounted yet (timing edge), we fail gracefully.
    //   2. The template stays readable.

    onCardDeleteProduct(cp) {
      const host = this.$refs.editHost;
      if (host) host.promptDeleteProduct(cp);
    },

    onCardStockLoaded(cp, info) {
      const host = this.$refs.editHost;
      if (host) host.showStockDialog(cp, info);
    },

    // Security reviewer P2 (2026-06-16): ProductCard used to mutate
    // its `campaignProduct` prop in place after the visibility-toggle
    // POST. It now emits this event with the new value; the parent
    // owns the write into the jsonapi-vuex proxy via the
    // campaignProducts array so reactivity stays clean.
    onCardHideFromStorefrontChanged({ campaignProductId, hidden }) {
      const target = this.campaignProducts.find(
        (cp) => cp && cp.id === campaignProductId,
      );
      if (target) {
        target.hide_from_storefront = hidden;
      }
    },

    // ---------- Phase 4.4 -- bulk action wiring ----------

    // Resolve the CP id from a row -- prefer _jv.id (jsonapi-vuex
    // canonical), fall back to the bare id column. Mirrors the
    // legacy pattern used by ProductCard.
    _cpId(cp) {
      if (!cp) return null;
      if (cp._jv && cp._jv.id) {
        const n = Number(cp._jv.id);
        return Number.isFinite(n) ? n : cp._jv.id;
      }
      if (cp.id !== undefined && cp.id !== null) {
        const n = Number(cp.id);
        return Number.isFinite(n) ? n : cp.id;
      }
      return null;
    },

    onCardSelectionToggle(cpId) {
      if (cpId === null || cpId === undefined) return;
      this.productsListSelection.toggle(cpId);
    },

    onBulkApplySet() {
      // Phase 4.6 -- open the apply-set modal. The DecorationSetPicker
      // is mounted inside the modal as the first step (per the plan,
      // we picked the cleaner "all-in-one" UX over a separate quick-
      // selector + modal sequence). The set id starts null; the
      // primary Apply button is disabled until the user picks one.
      if (this.bulkSelectedCount === 0) return;
      this.bulkApplySetId = null;
      this.bulkApplyModalOpen = true;
    },

    onBulkApplyApplied() {
      // P4.7 apply endpoint completed (when it ships). Refresh the
      // product list so any changed decorations are reflected; the
      // modal closes itself on apply success.
      this.productsListSelection.clear();
      this.loadCampaignProducts();
    },

    onBulkDuplicateClick() {
      if (this.bulkSelectedCount === 0) return;
      this.bulkDuplicateModalOpen = true;
    },

    onBulkVisibilityClick() {
      if (this.bulkSelectedCount === 0) return;
      this.bulkVisibilityModalOpen = true;
    },

    async onBulkVisibilitySubmit({ visible }) {
      if (this.bulkSubmitting) return;
      this.bulkSubmitting = true;
      const ids = this.productsListSelection.selectedIdArray.slice();
      try {
        // Patch each selected CP. The CampaignProduct model layer
        // syncs ``visible`` <-> ``hide_from_storefront`` automatically
        // (see campaign_product.py ``before_update`` listener), so we
        // only need to write the canonical ``visible`` column.
        await Promise.all(
          ids.map((cpId) =>
            this.$axios.patch(
              `/campaign_products/${cpId}`,
              {
                data: {
                  type: 'CampaignProduct',
                  id: String(cpId),
                  attributes: { visible: visible },
                },
              },
            ),
          ),
        );
        this.bulkVisibilityModalOpen = false;
        this.productsListSelection.clear();
        this.loadCampaignProducts();
      } catch (err) {
        console.error('Bulk visibility patch failed', describeError(err));
        this.$store.dispatch('raiseError', err);
      } finally {
        this.bulkSubmitting = false;
      }
    },

    async onBulkDuplicateSubmit({ campaignProductIds }) {
      if (this.bulkSubmitting) return;
      this.bulkSubmitting = true;
      try {
        await this.$axios.post(
          '/campaign_products/bulk_duplicate/',
          { campaign_product_ids: campaignProductIds },
        );
        this.bulkDuplicateModalOpen = false;
        this.productsListSelection.clear();
        this.loadCampaignProducts();
      } catch (err) {
        console.error('Bulk duplicate failed', describeError(err));
        this.$store.dispatch('raiseError', err);
      } finally {
        this.bulkSubmitting = false;
      }
    },

  },
};
</script>

<style scoped>
.add-container {
  display: flex;
}

/* Boulders: Products page: tabular view, https://trello.com/c/R0K6f6oC.
   Toolbar row -- Create Product CTA + card/table toggle. Toggle styling
   mirrors DecorationLibraryList.vue's dl-mode-toggle (same visual language
   for a List/Grid-style switch elsewhere in the distributor app). */
.products-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.products-view-toggle {
  display: flex;
  background: #eceff1;
  border-radius: 9px;
  padding: 3px;
  gap: 2px;
}

.products-view-toggle__btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 12.5px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 400;
  border-radius: 7px;
  cursor: pointer;
}

.products-view-toggle__btn--active {
  background: #fff;
  color: #212121;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.create-button-widget {
  height: 631px;
}

@media only screen and (min-width: 1904px) {
  .shopper-store-margin {
    max-width: 1600px;
  }
}

@media only screen and (min-width: 1264px) and (max-width: 1904px) {
  .shopper-store-margin {
    max-width: 1350px;
  }
}

@media only screen and (min-width: 960px) and (max-width: 1264px) {
  .shopper-store-margin {
    max-width: 870px;
  }
}

.divider {
  margin-top: 84px;
  margin-bottom: 40px;
}

.create-product-btn {
  margin-bottom: 34px;
  /* right: 25px; */
}

.campaign-products-container {
  position: relative;
}

/* Phase 4.4 -- per-card selection overlay. */
.bulk-select-card-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  border-radius: 8px;
  transition: box-shadow 0.15s ease;
}
.bulk-select-card-wrap--selected {
  box-shadow: 0 0 0 2px #6366f1;
}
.bulk-select-card-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 4;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
</style>
